import React from "react";
import ReactDOM from "react-dom";

export interface SearchGenerationModalState {
  phase: 'searching' | 'generating' | 'success' | 'error';
  query: string;
  difficulty?: string;
  message?: string;
  resultTitle?: string;
  timeElapsed?: number;
}

interface SearchGenerationModalProps {
  isOpen: boolean;
  onClose: () => void;
  state: SearchGenerationModalState;
}

export const SearchGenerationModal: React.FC<SearchGenerationModalProps> = ({
  isOpen,
  onClose,
  state
}) => {
  // Debug logging
  React.useEffect(() => {
    console.log('🎭 Modal state changed:', { isOpen, phase: state.phase, query: state.query });
  }, [isOpen, state.phase, state.query]);

  const getModalContent = () => {
    switch (state.phase) {
      case 'searching':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3">
              <div className="w-8 h-8 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></div>
            </div>
          ),
          title: "Searching for Problems",
          message: `Looking for existing problems matching "${state.query}"...`,
          subtitle: "This usually takes just a moment",
          bgColor: "bg-white",
          borderColor: "border-gray-300",
          textColor: "text-gray-700",
          iconColor: "text-gray-600"
        };

      case 'generating':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-gray-600">
              <svg className="w-8 h-8 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
          ),
          title: "Generating New Problem",
          message: `No existing problems found for "${state.query}"`,
          subtitle: `Creating ${state.difficulty ? `${state.difficulty} difficulty` : 'new'} problem with AI... (30-50 seconds)`,
          bgColor: "bg-white",
          borderColor: "border-gray-300", 
          textColor: "text-gray-700",
          iconColor: "text-gray-600"
        };

      case 'success':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-green-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          ),
          title: "Problem Ready!",
          message: `Generated: "${state.resultTitle}"`,
          subtitle: "Ready to solve!",
          bgColor: "bg-white",
          borderColor: "border-gray-300",
          textColor: "text-gray-700",
          iconColor: "text-green-600"
        };

      case 'error':
        return {
          icon: (
            <div className="w-8 h-8 mx-auto mb-3 text-red-600">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
          ),
          title: "Search Failed",
          message: state.message || "Something went wrong",
          subtitle: "Please try again",
          bgColor: "bg-white",
          borderColor: "border-gray-300",
          textColor: "text-gray-700",
          iconColor: "text-red-600"
        };

      default:
        return null;
    }
  };

  const showCloseButton = state.phase === 'success' || state.phase === 'error';
  const autoCloseDelay = state.phase === 'success' ? 4000 : 0;

  // Auto-close success modal after 4 seconds
  React.useEffect(() => {
    if (autoCloseDelay > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      return () => clearTimeout(timer);
    }
  }, [autoCloseDelay, onClose]);

  if (!isOpen) return null;

  const content = getModalContent();
  if (!content) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-gray-900 bg-opacity-10 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-2xl border-4 border-gray-400 p-6 max-w-md w-full mx-4`} style={{boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(0, 0, 0, 0.05)'}}>
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            {content.icon}
            <h3 className={`text-base font-semibold text-center ${content.textColor}`}>
              {content.title}
            </h3>
          </div>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="text-center">
          <p className={`text-sm ${content.textColor} mb-2 leading-relaxed`}>
            {content.message}
          </p>
          <p className={`text-xs text-gray-500`}>
            {content.subtitle}
          </p>

          {/* Time elapsed for generation */}
          {state.phase === 'generating' && state.timeElapsed && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                Time elapsed: {Math.floor(state.timeElapsed / 1000)}s
              </p>
              {state.timeElapsed > 15000 && (
                <p className="text-xs text-gray-500 mt-1">
                  Almost done... AI is crafting a quality problem for you
                </p>
              )}
            </div>
          )}

          {/* Progress bar for generation */}
          {state.phase === 'generating' && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div 
                  className="bg-gray-600 h-1.5 rounded-full transition-all duration-1000 ease-out"
                  style={{ 
                    width: state.timeElapsed 
                      ? `${Math.min(85, (state.timeElapsed / 30000) * 100)}%` 
                      : '10%' 
                  }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {state.phase === 'error' && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};